<template>
	<div class="account__section__container deposit">
		<payments-loader v-if="containerLoading" />
		<div v-else class="account__section global-card__type--block">
			<h3
				:class="{
					account__section__title: 1,
					deposit__title: 1,
				}"
			>
				{{ this.$t("account.deposit") }}
			</h3>
			<p
				:class="{
					account__section__desc: 1,
					deposit__desc: 1,
					'deposit__desc--error': context == 'error',
				}"
			>
				{{ this.$t("account.send-money-into-your-betika-account") }}
			</p>
			<error v-if="error">{{ error }}</error>

			<div class="deposit__amount-row">
				<button
					class="deposit__amount-btn"
					:disabled="transacting"
					:aria-label="$t('account.decrease-amount')"
					@click="decrementAmount"
				>−</button>
				<!--
					This screen intentionally uses a native input instead of the shared
					b-input component. The deposit amount input requires integer-only
					entry, regional min/max clamping, and blocking of "e", "E", "+", "-"
					and ".". The shared b-input does not enforce those rules and adds
					thousands-separator formatting that conflicts with the steppers and
					preset amount buttons used here. The custom +/- stepper layout also
					assumes a bare input element rather than the b-input wrapper.
				-->
				<input
					class="deposit__amount-input"
					name="deposit-amount"
					type="number"
					:min="env.minimumDepositAmount"
					:max="env.maximumDepositAmount"
					step="any"
					inputmode="decimal"
					autocomplete="off"
					:placeholder="$t('account.enter-amount-to-deposit')"
					:aria-label="$t('account.enter-amount-to-deposit')"
					:disabled="loading || transacting"
					v-model="amount"
					@keydown="handleAmountKeydown"
					@input="handleAmountInput"
				/>
				<button
					class="deposit__amount-btn"
					:disabled="transacting"
					:aria-label="$t('account.increase-amount')"
					@click="incrementAmount"
				>+</button>
			</div>

			<div class="deposit__meta-row">
				<p class="deposit__min-text">
					{{ $t("account.minimum") }}{{ env.minimumDepositAmount | currency(true) }}. {{ $t("account.tax-message") }}
				</p>
				<button
					v-if="amount"
					class="deposit__clear-btn"
					@click="clearAmount"
				>
					{{ $t("account.clear-amount") }}
				</button>
			</div>

			<template v-if="env.availableFeatures.paymentProviders">
				<h3 class="account__section__title">
					{{ $t("account.select-payment-provider") }}
				</h3>
				<b-checkbox
					:value="selectedProvider.name == provider.name"
					v-for="provider in depositProviders"
					:key="provider.name"
					:label="$options.filters.toTitleCase(provider.name)"
					:name="'provider'"
					@click.native="setProvider(provider)"
					class="session__form__checkbox session__form__remember"
					:disabled="loading"
				/>
			</template>

			<div class="deposit__stake-group">
				<button
					class="deposit__stake-btn"
					:disabled="transacting"
					v-for="value in depositButtons"
					:key="value"
					@click="addDepositAmount(value)"
				>
					+{{ value }}
				</button>
			</div>
			<div
				v-if="isMiniApp && miniappLoaded && isMiniappSupportedNetwork"
				class="deposit__miniapp-cta"
			>
				<b-button-ii
					:disabled="transacting"
					:loading="loading"
					:title="$t(env.miniapp.cta.title)"
					type="primary"
					rounded-small
					:fit-text="true"
					class="button account__section__submit deposit__mpesa-btn"
					@click="deposit"
				>
					<span class="deposit__mpesa-icon">
						<img :src="env.miniapp.cta.logoUrl" :alt="$t(env.miniapp.cta.title)" />
					</span>
				</b-button-ii>
			</div>
			<div v-else class="deposit__cta-row" :class="{ 'deposit__cta-row--cashia-deposit-disabled': !showCashiaDeposit }">
				<div
					class="deposit__cashia-wrapper"
					v-if="showCashiaDeposit"
				>
				<WalletCashia
					class="deposit__cashia-btn"
					:amount="amount"
					transactionType="Deposit"
					@validation-error="handleValidationError"
				/>
				</div>
				<div class="deposit__mpesa-wrapper">
					<b-button-ii
						:disabled="transacting"
						:loading="loading"
						:title="(isAirtelSupported || env.countryCode === 'CD') ? $t('account.deposit') : $t('account.deposit-with-mpesa')"
						type="primary"
						rounded-small
						:fit-text="true"
						:class="['button account__section__submit', isAirtelSupported ? 'deposit__airtel-btn' : 'deposit__mpesa-btn']"
						@click="deposit"
					>
						<span v-if="env.countryCode !== 'CD'" class="deposit__mpesa-icon">
							<img
								:src="isAirtelNetwork ? 'https://cdn.betika.com/int_assets/ke/icons/Airtel-icon-white.svg' : 'https://cdn.betika.com/int_assets/ke/icons/mpesa-icon.svg'"
								:alt="isAirtelNetwork ? 'Airtel' : 'M-Pesa'"
							/>
						</span>
					</b-button-ii>
				</div>
			</div>
			<span
				v-if="showCashiaDeposit"
				class="deposit__redesign_deposit_text"
				>
					{{$t("session.wallet.cashia.cashia-deposit-promotion-text")}}
			</span>
			<bottom-sheet
				:ref="'networkSelectBottomSheet'"
				v-if="
					canShowBottomSheet &&
					env.availableFeatures.network &&
					hasKyc
				"
				@close="closeBottomSheet"
				@has-mounted="showBottomSheet"
			>
				<div style="padding: 20px">
					<network-saver
						:available-networks="availableNetworks"
						:deposit-amount="amount"
						@close="close"
					/>
				</div>
			</bottom-sheet>
		</div>
	</div>
</template>

<script>
import PaymentsLoader from "./PaymentsLoader.vue";
import NetworkSaver from "./NetworkSaver.vue";
import WalletCashia from "./WalletCashia.vue";
import { parseRedesignDepositAmount } from "../../../utils/depositAmount";
import { env } from "../../../utils/env.js";

export default {
	components: {
		PaymentsLoader,
		NetworkSaver,
		WalletCashia,
	},
	props: {
		injectedAmount: {
			type: Number,
			required: false,
			default: null,
		},
		title: {
			type: String,
			required: false,
			default: "Deposit",
		},
		desc: {
			type: String,
			required: false,
			default: "Send money into your Betika account",
		},
		context: {
			type: String,
			required: false,
			default: "info",
		},
		user: {
			type: Object,
			required: true,
		},
		transacting: {
			type: Boolean,
			default: false,
		},
		containerLoading: {
			type: Boolean,
			default: false,
		},
	},
	created() {
		let vm = this;
		vm.$root.$on("perform-deposit", vm.performDeposit);
		vm.$root.$on("showNetworkSelector", vm.showBottomSheet);
	},
	beforeDestroy() {
		let vm = this;
		vm.$root.$off("perform-deposit", vm.performDeposit);
		vm.$root.$off("showNetworkSelector", vm.showBottomSheet);
		vm.$miniappDeposit.removeAppxScript();
	},
	computed: {
		hasKyc() {
			if (this.env.kyc.required === true) {
				return this.$store.getters.user.has_kyc;
			} else {
				return true;
			}
		},
		availableNetworks() {
			return this.env.networkProviders;
		},
		depositButtons() {
			return this.env.depositAmounts;
		},
		cashiaSupportsUserNetwork() {
			let vm = this;

			const token = vm.$auth.getToken();
			if (!token) {
				vm.$auth.logout();
				vm.$notifications.add({
					message: this.$t("session.expired"),
					type: "error",
					title: this.$t("session.session-expired"),
					duration: 5000,
				});
				vm.$router.replace("/login");
			}

			const userNetwork = this.$store.getters.user.network || "";

			if (!userNetwork) {
				return false;
			}

			const supportedExperimentNetworks = vm.env.cashia.supportedNetworks;
			return supportedExperimentNetworks.includes(userNetwork);
		},
		isAirtelNetwork() {
			return (this.$store.getters.user.network) === "airtel";
		},
		isAirtelSupported() {
			return this.isAirtelNetwork && ["KE", "CD"].includes(this.env.countryCode);
		},
		showCashiaDeposit() {
			return this.env.countryCode === 'KE'
				&& !!this.env.cashia.cashiaBaseUrl
				&& this.cashiaSupportsUserNetwork;
		},
	},
	data() {
		return {
			amount: null,
			loading: true,
			error: false,
			depositProviders: [{ name: "Loading" }, { name: "Loading" }],
			canShowBottomSheet: false,
			selectedProvider: {
				name: "interswitch",
			},
			miniappLoaded: false,
		};
	},
	async beforeMount() {
		let vm = this;

		if (vm.isMiniApp && vm.isMiniappSupportedNetwork) {
			const miniapp = await vm.$miniappDeposit.loadAppxScript(
				vm.env.miniapp.scriptSrc
			);
			vm.miniappLoaded = miniapp.scriptLoaded;
		}

		if (this.env.availableFeatures.paymentProviders) {
			this.getDepositProviders();
		} else {
			this.loading = false;
		}
		if (this.injectedAmount) this.amount = this.injectedAmount;
	},

	methods: {
		parseDepositAmount(amount = this.amount) {
			return parseRedesignDepositAmount(amount, this.env);
		},
		handleAmountKeydown(event) {
			if (
				event.key === "e" ||
				event.key === "E" ||
				event.key === "+" ||
				event.key === "-"
			) {
				event.preventDefault();
			}
		},
		handleAmountInput(event) {
			if (event.target.value === "") {
				this.amount = null;
				return;
			}

			const rawAmount = String(event.target.value);
			if (
				rawAmount.includes("e") ||
				rawAmount.includes("E") ||
				rawAmount.includes("+") ||
				rawAmount.includes("-")
			) {
				this.amount = null;
				event.target.value = "";
				return;
			}

			const parsedAmount = Number(rawAmount);
			if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
				this.amount = null;
				event.target.value = "";
				return;
			}

			this.amount = parsedAmount;
		},
		incrementAmount() {
			this.addDepositAmount(this.env.incrementDepositAmount);
		},
		addDepositAmount(value) {
			const amount = this.parseDepositAmount();
			const baseAmount = amount && amount > 0 ? amount : 0;
			this.amount = Math.min(
				baseAmount + value,
				this.env.maximumDepositAmount
			);
		},
		decrementAmount() {
			const amount = this.parseDepositAmount();
			const baseAmount = amount && amount > 0 ? amount : this.env.minimumDepositAmount;
			const newAmount = baseAmount - this.env.decrementDepositAmount;
			this.amount = Math.max(newAmount, this.env.minimumDepositAmount);
		},
		clearAmount() {
			this.amount = null;
		},
		showDepositAmountWarning(messageKey, amount) {
			return this.$notifications.add({
				message:
					this.$t(messageKey) +
					" " +
					this.$options.filters.currency(amount),
				title: this.$t("account.deposit-amount"),
				type: "warning",
				duration: 5000,
			});
		},
		setProvider(provider) {
			this.selectedProvider = provider;
		},
		getDepositProviders() {
			let vm = this;
			vm.$axios
				.get(`${BASE_V2_URL}deposit-providers`)
				.then((res) => {
					vm.depositProviders = res.data;
					vm.loading = false;
				})
				.catch((err) => {
					vm.error = true;
					vm.loading = false;
				});
		},
		deposit() {
			let vm = this;
			vm.error = false;
			if (vm.loading || vm.transacting) return;
			if (!vm.selectedProvider.name) return;

			const parsedAmount = this.parseDepositAmount();

			if (parsedAmount === null) {
				vm.error = this.$t("account.please-enter-an-amount");
				return;
			}

			if (parsedAmount < vm.env.minimumDepositAmount) {
				return vm.showDepositAmountWarning(
					"account.minimum-deposit-amount-is",
					vm.env.minimumDepositAmount
				);
			}

			if (parsedAmount > vm.env.maximumDepositAmount) {
				return vm.showDepositAmountWarning(
					"account.maximum-deposit-amount-is",
					vm.env.maximumDepositAmount
				);
			}

			vm.amount = parsedAmount;
			vm.loading = true;

			const token = vm.$auth.getToken();
			if (vm.env.availableFeatures.network) {
				vm.$axios
					.post(`${BASE_URL}balance`, { token })
					.then((response) => {
						vm.$store.commit("setBalance", response.data.data.balance);
						vm.$store.commit("setKyc", response.data.data.has_kyc);
						vm.$store.commit("SET_KYC_DATA", response.data.data.kyc);

						vm.network = response.data.data.network;

						if (
							vm.env.availableFeatures.network &&
							(vm.network == null || vm.network == "")
						) {
							this.$nextTick(() => {
								vm.showBottomSheet();
							});
							this.$nextTick(() => {
								this.$root.$emit(
									"close-deposit-open-network-selector",
									parsedAmount
								);
							});
							return;
						}

						if (
							vm.miniappLoaded &&
							vm.isMiniApp &&
							vm.isMiniappSupportedNetwork
						) {
							let mobile = vm.user.mobile;
							let depositAmount = parsedAmount;
							let paybillNo = vm.env.miniapp.businessId;
							let currency = vm.env.currency;
							vm.performMiniappDeposit(
								mobile,
								depositAmount,
								paybillNo,
								currency
							);
							return;
						}
						vm.performDeposit(parsedAmount);
					})
					.catch((error) => {
						vm.error = true;
						vm.loading = false;
						if (error.response.status == 401) {
							vm.$auth.logout();
							vm.$router.replace("/login");
							vm.$notifications.add({
								message: this.$t("session.expired"),
								type: "error",
								title: this.$t("session.session-expired"),
								duration: 5000,
							});
							return;
						}
					});
			} else {
				if (
					vm.miniappLoaded &&
					vm.isMiniApp &&
					vm.isMiniappSupportedNetwork
				) {
					let mobile = vm.user.mobile;
					let depositAmount = parsedAmount;
					let paybillNo = vm.env.miniapp.businessId;
					let currency = vm.env.currency;
					vm.performMiniappDeposit(
						mobile,
						depositAmount,
						paybillNo,
						currency
					);
					return;
				}
				vm.performDeposit(parsedAmount);
			}
		},
		showBottomSheet() {
			this.canShowBottomSheet = true;
			this.loading = false;
			this.$emit("is-transacting", false);
			this.$nextTick(() => {
				this.$refs.networkSelectBottomSheet.setState("open");
			});
		},
		closeBottomSheet() {
			this.$refs.networkSelectBottomSheet.setState("close");
			this.loading = false;
			this.$emit("is-transacting", false);
			this.$nextTick(() => {
				this.canShowBottomSheet = false;
			});
		},
		performDeposit(depositAmount = null) {
			let vm = this;
			if (depositAmount != null) {
				vm.amount = depositAmount;
			}

			let appSrc = vm.$utils.getAppSource();

			const token = vm.$auth.getToken();
			let payload = {
				src: appSrc,
				amount: vm.amount,
				token,
			};

			if (
				vm.env.availableFeatures.paymentProviders &&
				isNetworkSelected == false
			) {
				payload.provider = vm.selectedProvider.name;
			}

			if(
				vm.env.availableFeatures.network &&
				(vm.network !== null && vm.network !== "")
			){
				payload.provider = vm.network;
			}

			this.analyticsTrack("deposit", "attempt", {
				mobile: vm.user.mobile,
				user_id: vm.user.id,
				wallet_balance: vm.user.balance,
				depositAmount: vm.$options.filters.currency(vm.amount),
				depositMethod: "ONLINE",
			});

			vm.$emit("is-transacting", true);
			let url =
				vm.env.depositWithdrawVersion == "v1"
					? `${BASE_URL}deposit`
					: `${BASE_V2_URL}deposit`;

			let successMessage = this.$t(
				"account.success-please-check-your-phone-to-complete-the-deposit"
			);
			let errorMessage = "";

			vm.$axios
				.post(url, payload)
				.then((response) => {
					vm.loading = false;
					vm.$emit("is-transacting", false);
					vm.$notifications.add({
						message: successMessage,
						type: "success",
						title: this.$t("account.deposit-initiated"),
						duration: 15000,
					});
					this.analyticsTrack("deposit", "success", {
						mobile: vm.user.mobile,
						user_id: vm.user.id,
						wallet_balance: vm.user.balance,
						depositAmount: vm.$options.filters.currency(vm.amount),
						depositMethod: "ONLINE",
						message: "Success",
						isFirstDeposit: 0,
						value: Number(vm.amount) || 0,
						currency: window.CURRENCY,
					});
					vm.amount = null;
					vm.$emit("close");
					if (response.data.url) window.open(response.data.url);
				})
				.catch((error) => {
					vm.loading = false;
					vm.$emit("is-transacting", false);
					this.analyticsTrack("deposit", "failed", {
						mobile: vm.user.mobile,
						user_id: vm.user.id,
						wallet_balance: vm.user.balance,
						depositAmount: vm.$options.filters.currency(vm.amount),
						depositMethod: "ONLINE",
						message: error.response
							? error.response.data.error.message + errorMessage
							: error.message + errorMessage,
					});
					if (error.response) {
						if (error.response.status == 401) {
							vm.$auth.logout();
							vm.$router.replace("/login");
							return;
						}
						try {
							vm.$notifications.add({
								message: error.response.data.error.message + errorMessage,
								type: "error",
								title: this.$t("account.deposit-failed"),
								duration: 5000,
							});
						} catch (e) {
							vm.$notifications.add({
								message:
									this.$t("session.an-error-occured-please-try-again") +
									errorMessage,
								type: "error",
								title: this.$t("account.deposit-failed"),
								duration: 5000,
							});
						}
					} else {
						vm.$notifications.add({
							message:
								this.$t("session.an-error-occured-please-try-again") +
								errorMessage,
							type: "error",
							title: this.$t("account.deposit-failed"),
							duration: 5000,
						});
					}
				});
		},
		async performMiniappDeposit(mobile, depositAmount, payBillNo, currency) {
			let vm = this;

			let successMessage = vm.$t(
				"account.success-please-check-your-phone-to-complete-the-deposit"
			);

			let errorMessage = vm.$t("session.an-error-occured-please-try-again");

			const response = await vm.$miniappDeposit.miniAppDeposit(
				mobile,
				depositAmount,
				payBillNo,
				currency
			);
			if (response) {
				vm.loading = false;
				vm.$emit("is-transacting", false);
				vm.$notifications.add({
					message: successMessage,
					type: "success",
					title: this.$t("account.deposit-initiated"),
					duration: 15000,
				});
				this.analyticsTrack("deposit", "success", {
					mobile: vm.user.mobile,
					user_id: vm.user.id,
					wallet_balance: vm.user.balance,
					depositAmount: vm.$options.filters.currency(vm.amount),
					depositMethod: "ONLINE",
					message: "Success",
					isFirstDeposit: 0,
					value: Number(vm.amount) || 0,
					currency: window.CURRENCY,
				});
				vm.amount = null;
				vm.$emit("close");

				return true;
			}

			vm.loading = false;
			vm.$emit("is-transacting", false);
			this.analyticsTrack("deposit", "failed", {
				mobile: vm.user.mobile,
				user_id: vm.user.id,
				wallet_balance: vm.user.balance,
				depositAmount: vm.$options.filters.currency(vm.amount),
				depositMethod: "ONLINE",
				message: "Miniapp deposit failed",
			});

			vm.$notifications.add({
				message: errorMessage,
				type: "error",
				title: this.$t("account.deposit-failed"),
				duration: 5000,
			});

			return false;
		},
		close() {
			this.closeBottomSheet();
		},
		handleValidationError(message) {
			this.error = message;
		},
	},
};
</script>
<style lang="scss" scoped>
.global-card__type--block {
	border-color: var(--deposit-card-border-color);
}

.deposit {
&__redesign_deposit_text{
	// display: block;
	// margin-top:6px;
	// font-weight: 600;
	// font-size: 12px;
	color:var(--bg-rose-600);
	display: block;
	margin-top:8px;
	font-size: 14px;
    line-height: 20px;
    font-weight: var(--font-weight-light);
}
	margin-bottom: 15px;
	&__desc {
		color: var(--gray-600);

		&--error {
			color: var(--red);
			background: var(--light-red);
			padding: 5px;
			border-radius: 5px;
		}
	}
	&__cta-row {
		display: flex;
		gap: 8px;
		align-items: stretch;

		&--cashia-deposit-disabled {
			.deposit__mpesa-wrapper {
				flex: 1 1 100%;
				max-width: 100%;
			}
		}
	}

	&__mpesa-wrapper {
		flex: 1;
		min-width: 0;
	}

	&__mpesa-btn {
		width: 100%;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		padding: 9px 10px !important;
		box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
		background-color: var(--deposit-green) !important;
		border-color: var(--deposit-green) !important;

		::v-deep .btntitle {
			font-size: 11px;
			font-weight: 600;
			line-height: 16px;
			color: white;
			white-space: nowrap;
		}
	}

	&__airtel-btn {
		display: flex;
		width: 100%;
		height: 32px;
		padding: 8px var(--spacing-3, 12px) !important;
		justify-content: center;
		align-items: center;
		gap: var(--spacing-1, 4px);
		align-self: stretch;
		box-sizing: border-box;
		border-radius: 8px !important;
		background-color: #B91C1C !important;
		border-color: #B91C1C !important;
		box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);

		::v-deep .btntitle {
			color: var(--tailwind-colors-zinc-50, #FAFAFA);
			font-family: var(--typography-font-family-font-sans, Inter);
			font-size: var(--typography-base-sizes-extra-small-font-size, 12px);
			font-style: normal;
			font-weight: var(--font-weight-semibold, 600);
			line-height: var(--typography-base-sizes-extra-small-line-height, 16px);
			white-space: nowrap;
		}
	}

	&__mpesa-icon {
		flex-shrink: 0;
		width: 18px;
		height: 18px;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 18px;
			height: 18px;
		}
	}

	&__cashia-wrapper {
		flex: 1;
		min-width: 0;
	}

	::v-deep .deposit__cashia-btn {
		width: 100%;
		height: 100%;

		.wallet-gateway {
			margin: 0;
			padding: 0;
			width: 100%;

			> p {
				display: none;
			}

			.btn {
				box-sizing: border-box;
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 6px;
				padding: 9px 10px !important;
				border-radius: 8px;
				box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
				width: 100%;
			}

			.btn-error {
				background-color: var(--bg-rose-600);
				border-color: var(--bg-rose-600);
			}

			.btntitle {
				font-size: 11px;
				font-weight: 600;
				line-height: 16px;
				color: white;
				white-space: nowrap;
			}

			svg, img {
				width: 18px;
				height: 18px;
				flex-shrink: 0;
			}
		}
	}

	&__stake-group {
		display: flex;
		justify-content: space-between;
		gap: 16px;
		margin-top: 0;
		margin-bottom: 14px;
	}

	&__stake-btn {
		flex: 1;
		background: var(--stake-btn-bg);
		color: var(--stake-btn-text);
		border: 1px solid var(--stake-btn-border-color);
		border-radius: 40px;
		padding: 8px 14px;
		font-size: 14px;
		font-weight: 600;
		line-height: 20px;
		cursor: pointer;
		white-space: nowrap;
		transition: opacity 0.15s;

		&:disabled {
			opacity: 0.4;
			cursor: default;
		}

		&:active {
			background: var(--deposit-green);
		}
	}

	&__amount-row {
		display: flex;
		align-items: center;
		background: var(--white);
		border: 1px solid var(--stake-input-border-color);
		border-radius: 8px;
		box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
		padding: 8px 12px;
		gap: 8px;
		margin-top: 15px;
		margin-bottom: 6px;
	}

	&__amount-btn {
		background: transparent;
		border: none;
		width: 24px;
		height: 24px;
		font-size: 18px;
		line-height: 1;
		cursor: pointer;
		color: var(--black);
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0;

		&:disabled {
			opacity: 0.4;
			cursor: default;
		}
	}

	&__amount-input {
		flex: 1;
		border: none;
		text-align: center;
		background: transparent;
		font-size: 14px;
		line-height: 20px;
		outline: none;
		color: var(--black);
		-moz-appearance: textfield;

		&::placeholder {
			color: var(--light-black-200);
			font-size: 14px;
		}

		&::-webkit-outer-spin-button,
		&::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}
	}

	&__meta-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 10px;
	}

	&__min-text {
		margin: 0;
    color: var(--light-dark-blue);
    font-weight: var(--font-weight-lighter);
	}

	&__clear-btn {
		font-size: 14px;
		line-height: 20px;
		font-weight: 400;
		color: var(--black);
		text-decoration: underline;
		cursor: pointer;
		background: none;
		border: none;
		padding: 0;
	}
}

html:not(.dark) .deposit__min-text {
	color: #475467;
}
</style>
